import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Search,
  Trash2,
  Phone,
  Mail,
  Calendar,
  MessageSquare,
  ExternalLink,
  CheckCircle2,
} from "lucide-react";

const STATUSES = [
  { v: "pending", label: "Pendiente", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { v: "contacted", label: "Contactado", color: "bg-sky-100 text-sky-800 border-sky-200" },
  { v: "scheduled", label: "Agendado", color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { v: "done", label: "Atendido", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { v: "cancelled", label: "Cancelado", color: "bg-zinc-100 text-zinc-700 border-zinc-200" },
];

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const AdminDashboard = () => {
  const { user, loading, logout, authedFetch } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await authedFetch("/appointments");
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("No se pudo cargar la lista.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await authedFetch(`/appointments/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: { status },
      });
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status } : it))
      );
      toast.success("Estado actualizado");
    } catch {
      toast.error("No se pudo actualizar.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cita? Esta acción no se puede deshacer.")) return;
    try {
      await authedFetch(`/appointments/${id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Cita eliminada");
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  const exportCsv = () => {
    const headers = ["fecha", "nombre", "email", "telefono", "servicio", "fecha_preferida", "estado", "mensaje"];
    const rows = filtered.map((a) =>
      [a.created_at, a.name, a.email, a.phone, a.service, a.preferred_date || "", a.status, (a.message || "").replace(/\n/g, " ")]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `multident-citas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((it) => {
      if (filter !== "all" && it.status !== filter) return false;
      if (!ql) return true;
      return [it.name, it.email, it.phone, it.service, it.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(ql));
    });
  }, [items, q, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach((s) => (c[s.v] = items.filter((i) => i.status === s.v).length));
    return c;
  }, [items]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] text-[#666]">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F8F6] text-[#1A1A1A]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="/" className="font-display text-xl tracking-tight flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#D4AF37]" />
            Multident · Panel
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-[#666] tracking-widest uppercase">
              {user?.email}
            </span>
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[#1A1A1A]/15 rounded-full px-4 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Title row */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="overline mb-2">— Citas recibidas</p>
            <h1 className="font-display font-light text-3xl md:text-4xl tracking-tight">
              {items.length} solicitudes en total
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="refresh-btn"
              onClick={load}
              disabled={refreshing}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[#1A1A1A]/15 rounded-full px-4 py-2 bg-white hover:border-[#D4AF37] disabled:opacity-50"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refrescar
            </button>
            <button
              data-testid="export-csv"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[#1A1A1A]/15 rounded-full px-4 py-2 bg-white hover:border-[#D4AF37]"
            >
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <FilterPill
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label="Todos"
            count={counts.all}
          />
          {STATUSES.map((s) => (
            <FilterPill
              key={s.v}
              active={filter === s.v}
              onClick={() => setFilter(s.v)}
              label={s.label}
              count={counts[s.v]}
            />
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]"
          />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, email, teléfono, servicio…"
            className="w-full pl-11 pr-4 py-3 bg-white border border-[#E5E5E5] rounded-full text-sm outline-none focus:border-[#D4AF37]"
          />
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div
            data-testid="empty-state"
            className="bg-white border border-dashed border-[#E5E5E5] rounded-3xl p-16 text-center text-[#666]"
          >
            {items.length === 0
              ? "Aún no llegan citas. Cuando alguien agende desde el sitio, aparecerá aquí y recibirás un correo."
              : "No hay resultados con esos filtros."}
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((a) => (
              <AppointmentCard
                key={a.id}
                a={a}
                onStatus={updateStatus}
                onRemove={remove}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const FilterPill = ({ active, onClick, label, count }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest rounded-full px-4 py-2 transition-colors border ${
      active
        ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
        : "bg-white text-[#1A1A1A] border-[#E5E5E5] hover:border-[#D4AF37]"
    }`}
  >
    {label}
    <span className={`text-[10px] ${active ? "text-white/70" : "text-[#666]"}`}>
      {count}
    </span>
  </button>
);

const AppointmentCard = ({ a, onStatus, onRemove }) => {
  const status = STATUSES.find((s) => s.v === a.status) || STATUSES[0];
  const waLink = `https://wa.me/${a.phone.replace(/\D/g, "")}?text=${encodeURIComponent(
    `Hola ${a.name}, te escribimos de Multident sobre tu solicitud de ${a.service}.`
  )}`;

  return (
    <article
      data-testid={`appointment-card-${a.id}`}
      className="bg-white border border-[#E5E5E5] rounded-3xl p-6 md:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-display text-xl tracking-tight">{a.name}</h3>
            <span
              className={`inline-flex items-center text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border ${status.color}`}
            >
              {status.label}
            </span>
          </div>
          <p className="text-xs text-[#666] uppercase tracking-widest mt-1">
            {fmt(a.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid={`status-select-${a.id}`}
            value={a.status}
            onChange={(e) => onStatus(a.id, e.target.value)}
            className="text-xs px-3 py-2 border border-[#E5E5E5] rounded-full bg-white outline-none focus:border-[#D4AF37]"
          >
            {STATUSES.map((s) => (
              <option key={s.v} value={s.v}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            data-testid={`delete-${a.id}`}
            onClick={() => onRemove(a.id)}
            className="p-2 rounded-full border border-[#E5E5E5] hover:border-red-300 hover:text-red-600 transition-colors"
            title="Eliminar"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <Info icon={<Mail size={14} />} label="Email">
          <a href={`mailto:${a.email}`} className="hover:text-[#D4AF37]">
            {a.email}
          </a>
        </Info>
        <Info icon={<Phone size={14} />} label="Teléfono">
          <a href={`tel:${a.phone}`} className="hover:text-[#D4AF37]">
            {a.phone}
          </a>
        </Info>
        <Info icon={<CheckCircle2 size={14} />} label="Servicio">
          {a.service}
        </Info>
        <Info icon={<Calendar size={14} />} label="Fecha preferida">
          {a.preferred_date || "—"}
        </Info>
        {a.message && (
          <Info icon={<MessageSquare size={14} />} label="Mensaje" full>
            <span className="whitespace-pre-wrap">{a.message}</span>
          </Info>
        )}
      </div>

      <div className="mt-6 pt-5 border-t border-[#E5E5E5] flex flex-wrap gap-2">
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded-full bg-[#25D366] text-white hover:opacity-90"
        >
          WhatsApp <ExternalLink size={12} />
        </a>
        <a
          href={`mailto:${a.email}`}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded-full border border-[#E5E5E5] hover:border-[#D4AF37]"
        >
          Responder por email
        </a>
        <a
          href={`tel:${a.phone}`}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 rounded-full border border-[#E5E5E5] hover:border-[#D4AF37]"
        >
          Llamar
        </a>
      </div>
    </article>
  );
};

const Info = ({ icon, label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#666] mb-1">
      <span className="text-[#D4AF37]">{icon}</span>
      {label}
    </div>
    <div className="text-[#1A1A1A]">{children}</div>
  </div>
);
